import { authFetch } from '../lib/authFetch';
import { buildQueryString } from '../lib/query';
import { dedupeRequest } from '../lib/requestDeduplication';
import { resolveBase, json } from './client';
import type { RecentRecordEntry, TrackRecentRecordPayload } from '../types/recentRecords';
import type { ApiErrorPayload } from './contracts';

type RecentRecordsApiResponse = ApiErrorPayload & {
  items?: RecentRecordEntry[];
  item?: RecentRecordEntry | null;
};

type FetchOptions = {
  limit?: number;
  signal?: AbortSignal;
};

export async function fetchRecentRecords(apiBase: string, options?: FetchOptions): Promise<RecentRecordEntry[]> {
  const base = resolveBase(apiBase);
  const query = buildQueryString({ limit: options?.limit }, { includeQuestionMark: true });
  const url = `${base}/api/recent-records${query}`;

  const run = async () => {
    const response = await authFetch(url, { signal: options?.signal });
    const data = await json<RecentRecordsApiResponse>(response);
    if (data?.status === 'success') {
      return data.items || [];
    }
    if (Array.isArray(data?.items)) {
      return data.items;
    }
    throw new Error(data?.message || 'Failed to load recent records');
  };

  if (options?.signal) {
    return run();
  }

  return dedupeRequest(url, run);
}

type TrackOptions = {
  signal?: AbortSignal;
};

export async function trackRecentRecord(
  apiBase: string,
  payload: TrackRecentRecordPayload,
  options?: TrackOptions,
): Promise<RecentRecordEntry | null> {
  const base = resolveBase(apiBase);
  const response = await authFetch(`${base}/api/recent-records`, {
    method: 'POST',
    body: JSON.stringify(payload),
    signal: options?.signal,
  });
  const data = await json<RecentRecordsApiResponse>(response);
  if (data?.status === 'success') {
    return data.item || null;
  }
  if (data?.item) {
    return data.item as RecentRecordEntry;
  }
  if (data?.message) {
    throw new Error(data.message);
  }
  return null;
}
